// Toolbar výpisu (issue #13, screens/04-category.md §5, 02-komponenty.md §10):
// pilulkové sort taby a rozsah „1–12 z 12".
//
// Řazení zůstává nativní — tab jen klikne na odpovídající `.listSorting__control`
// (default JS na něm visí AJAXem). Výchozí „Doporučujeme" nativní tlačítko nemá
// (je to stav bez `?order=`), takže je to odkaz na URL bez parametru.
// Přepínač mřížka/seznam z handoveru (§5 ho dovoluje vypustit) je od 8. 10. 2026
// pryč i z mobilní lišty (Tomáš): výpis je vždy mřížka a dřívější volba
// `bsListingView` v localStorage nemá čtenáře, takže nic nepřepne.
//
// Karty (badgy, cenová pilulka, hodnocení) má na starost productCard.js, zbytek
// stránky (filtr, sloupce, SEO blok) categoryPage.js.
// Výpis z vlastního indexu (saleBrowse.js) se registruje v listingSource.js:
// volby řazení i rozsah pak dodává on a stránkování je jeho „Načíst další".
// Idempotentní, re-init po AJAX překreslení (filtr) přes onShoptetDom.

import { iconHtml } from './icon.js';
import { listingStats } from './categoryPage.js';
import { listingSource } from './listingSource.js';
import L from '../locale/cs/listingTools.js';

const LABEL_DEFAULT_SORT = L.defaultSort;

function text(el) {
  return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

// „Doporučujeme" = výchozí řazení, tedy URL bez `?order=`
function defaultSortHref() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('order');
    return url.pathname + url.search + url.hash;
  } catch (e) {
    return window.location.pathname;
  }
}

// „1–12 z 350": na poslední straně dopočítáme od celku, jinak od velikosti strany
// (počet vykreslených karet = plná strana). Obojí vychází přesně.
function rangeText() {
  const s = listingStats();
  if (!isFinite(s.total) || !s.rendered) return '';
  // vlastní index dosypává karty od začátku výběru („1–48 z 623")
  if (listingSource()) return L.range(1, s.rendered, s.total);
  let start;
  let end;
  if (s.page >= s.pages) {
    end = s.total;
    start = Math.max(1, s.total - s.rendered + 1);
  } else {
    start = (s.page - 1) * s.rendered + 1;
    end = start + s.rendered - 1;
  }
  return L.range(start, end, s.total);
}

// Volby řazení z nativní vrstvy. Sdílí je desktopové taby i mobilní bottom-sheet
// (mobileFilter.js) — jediné čtení `.listSorting`, ať se popisky nerozejdou.
export function sortOptions() {
  const source = listingSource();
  if (source) return source.sortOptions();
  const controls = Array.prototype.slice.call(document.querySelectorAll('.listSorting__control'));
  if (!controls.length) return [];

  const current = text(document.querySelector('.sortingToggle__value'));
  const options = [
    {
      label: LABEL_DEFAULT_SORT,
      href: defaultSortHref(),
      active: current === LABEL_DEFAULT_SORT,
      activate: function () {
        window.location.href = defaultSortHref();
      },
    },
  ];

  controls.forEach(function (ctl) {
    const label = text(ctl);
    if (!label) return;
    options.push({
      label: label,
      href: null,
      active: label === current,
      // tlačítko dohledat živě — default JS `.listSorting` při AJAX řazení
      // může vyměnit a držená reference by klikala do odpojeného uzlu (B3-02)
      activate: function () {
        const live = liveSortControl(label);
        (live || ctl).click();
      },
    });
  });

  return options;
}

function liveSortControl(label) {
  const controls = document.querySelectorAll('.listSorting .listSorting__control');
  for (let i = 0; i < controls.length; i++) {
    if (text(controls[i]) === label) return controls[i];
  }
  return null;
}

// Re-init nad existujícím toolbarem: dorovnat aktivní tab a skrytí nativních
// prvků, které AJAX řazení/filtr mohl překreslit (B3-02).
function syncToolbar() {
  if (listingSource()) {
    rebuildSortTabs();
    return;
  }
  // zdroj odešel (saleBrowse bez odpovědi indexu): jeho taby nic neřadí, zpět nativní
  if (document.querySelector('.bs-toolbar .bs-sort-tabs[data-bs-source]')) rebuildSortTabs();
  const current = text(document.querySelector('.sortingToggle__value'));
  if (current) {
    document.querySelectorAll('.bs-sort-tabs .bs-sort-tab').forEach(function (tab) {
      tab.classList.toggle('is-active', tab.textContent === current);
    });
  }
  const sorting = document.querySelector('.listSorting');
  if (sorting && document.querySelector('.bs-sort-tabs')) sorting.classList.add('bs-hidden-native');
  const pagination = document.querySelector('.category-header-pagination');
  if (pagination) pagination.classList.add('bs-hidden-native');
}

function buildSortTabs() {
  const options = sortOptions();
  if (!options.length) return null;

  const tabs = document.createElement('div');
  tabs.className = 'bs-sort-tabs';
  if (listingSource()) tabs.dataset.bsSource = '1';

  options.forEach(function (opt) {
    // výchozí řazení je stav bez `?order=` → odkaz, ostatní klikají nativní tlačítko
    const tab = document.createElement(opt.href ? 'a' : 'button');
    if (opt.href) tab.href = opt.href;
    else {
      tab.type = 'button';
      tab.addEventListener('click', opt.activate);
    }
    tab.className = 'bs-sort-tab';
    tab.textContent = opt.label;
    if (opt.active) tab.classList.add('is-active');
    tabs.appendChild(tab);
  });

  return tabs;
}

// Taby z voleb zdroje výpisu: aktivní volbu zná jen on (nativní `.sortingToggle__value`
// se při řazení indexu nemění), proto se po každé změně staví znovu.
function rebuildSortTabs() {
  const old = document.querySelector('.bs-toolbar .bs-sort-tabs');
  const tabs = buildSortTabs();
  if (old && tabs) old.replaceWith(tabs);
  else if (tabs) {
    const bar = document.querySelector('.bs-toolbar');
    if (bar) bar.insertAdjacentElement('afterbegin', tabs);
  }
}

function buildToolbar() {
  if (document.querySelector('.bs-toolbar')) {
    syncToolbar();
    return;
  }

  const sorting = document.querySelector('.listSorting');
  const header = document.querySelector('#category-header');
  const host = header || (sorting && sorting.parentElement);
  if (!host) return;

  const bar = document.createElement('div');
  bar.className = 'bs-toolbar';

  const tabs = buildSortTabs();
  if (tabs) {
    bar.appendChild(tabs);
    if (sorting) sorting.classList.add('bs-hidden-native');
  }

  const spacer = document.createElement('span');
  spacer.className = 'bs-toolbar__spacer';
  bar.appendChild(spacer);

  const range = document.createElement('span');
  range.className = 'bs-toolbar__range';
  bar.appendChild(range);

  // nativní „Stránka 1 z 30 - 350 položek celkem" nahrazuje rozsah v toolbaru
  const pagination = document.querySelector('.category-header-pagination');
  if (pagination) pagination.classList.add('bs-hidden-native');

  if (header) header.appendChild(bar);
  else sorting.insertAdjacentElement('beforebegin', bar);
}

function updateRange() {
  const out = document.querySelector('.bs-toolbar__range');
  if (out) out.textContent = rangeText();
}

// ---------------------------------------------------------------------------
// Mobilní stránkování (m04 §8) — „Načíst dalších N" je nativní `.loadMore`
// (jen restyle), pod ním řádek šipek 44×44 s textem „Strana X z Y".
// Nativní číselný seznam na mobilu schová CSS. Šipky = nativní odkazy prev/next,
// chybějící strana zůstane jako neaktivní placeholder bez handleru.
// ---------------------------------------------------------------------------
const LABEL_PREV = L.prevPage;
const LABEL_NEXT = L.nextPage;

// Velikost stránky přesně z nativní hlavičky („X položek celkem" / počet stran)
function pageSize(s) {
  if (isFinite(s.total) && s.pages > 1) return Math.ceil(s.total / s.pages);
  return s.rendered || 0;
}

function pagerArrow(dir, native) {
  const node = document.createElement(native ? 'a' : 'span');
  node.className = 'bs-mpager__arrow bs-mpager__arrow--' + dir + (native ? '' : ' is-disabled');
  if (native) {
    node.href = native.getAttribute('href');
    node.setAttribute('aria-label', dir === 'prev' ? LABEL_PREV : LABEL_NEXT);
  } else {
    node.setAttribute('aria-hidden', 'true');
  }
  node.innerHTML = iconHtml(dir === 'prev' ? 'arrow-left' : 'arrow-right');
  return node;
}

function buildPager() {
  const pagination = document.querySelector('nav.pagination');
  if (!pagination) return;
  if (listingSource()) {
    // stránkuje „Načíst další" vlastního indexu, nativní šipky by vedly na jinou stranu
    const pager = document.querySelector('.bs-mpager');
    if (pager) pager.remove();
    return;
  }

  const old = document.querySelector('.bs-mpager');
  if (old) old.remove(); // po překreslení se mění i odkazy stran

  // číselný seznam schová CSS až pod touhle třídou — bez JS zůstane nativní
  pagination.classList.add('bs-pag-native');

  const pager = document.createElement('div');
  pager.className = 'bs-mpager';
  pager.appendChild(pagerArrow('prev', pagination.querySelector('.pagination__link--prev')));
  const label = document.createElement('span');
  label.className = 'bs-mpager__label';
  pager.appendChild(label);
  pager.appendChild(pagerArrow('next', pagination.querySelector('.pagination__link--next')));
  pagination.insertAdjacentElement('afterend', pager);
}

// loadMore dosype další stranu do stejného výpisu → „Strany 1–2 z 30"
function updatePager() {
  const label = document.querySelector('.bs-mpager__label');
  if (!label) return;
  const s = listingStats();
  if (!isFinite(s.total) || !s.pages) return;
  const size = pageSize(s);
  const loaded = size ? Math.max(1, Math.ceil(s.rendered / size)) : 1;
  const from = s.page;
  const to = Math.min(s.pages, from + loaded - 1);
  label.textContent = from === to ? L.page(from, s.pages) : L.pages(from, to, s.pages);
}

// loadMore dosype karty AJAXem a ShoptetDOM* event u něj NEstřílí (viz CLAUDE.md)
function observeProducts(products) {
  if (products.dataset.bsRangeObserved) return;
  products.dataset.bsRangeObserved = '1';
  new MutationObserver(function () {
    updateRange();
    updatePager();
  }).observe(products, { childList: true });
}

/** Dorovná toolbar (taby, rozsah) po změně výpisu mimo nativ. */
export function refreshListingTools() {
  if (!document.querySelector('.bs-toolbar')) return;
  syncToolbar();
  buildPager();
  updateRange();
}

export function initListingTools() {
  const products = document.querySelector('#products.products-page');
  if (!products) return;

  buildToolbar();
  buildPager();
  updateRange();
  updatePager();
  observeProducts(products);
}
